import React, { useRef } from 'react';
import { func, object, oneOf, string } from 'prop-types';
import classNames from 'classnames';
import { InlineTextButton } from '..';
import css from './FieldPermitAndCertificate.module.css';
import { languageSelectOptions } from '../../marketplace-custom-config';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FieldArray } from 'react-final-form-arrays';
import { BsPlusLg } from 'react-icons/bs';
import { FiEdit3, FiMinus } from 'react-icons/fi';
import { PermitAndCertificateForm } from '../../forms';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

const customLabel = Object.values(languageSelectOptions).reduce((pre, curnt) => {
  pre[curnt.value] = string.isRequired;
  return pre;
}, {});

const nativeLangOptions = Object.keys(customLabel);

const FieldPermitAndCertificateComponent = props => {
  const {
    rootClassName,
    className,
    errorClassName,
    titleClassName,
    id,
    name,
    userNativeLang,
    intl,
    formApi,
    formError,
  } = props;

  const preSavedValues = useRef({});

  const addMoreCert = e => {
    e.preventDefault();
    e.stopPropagation();
    const {
      mutators: { push },
    } = formApi;
    push(name, { isEditMode: true, isSavedOnce: false, id: uuidv4() });
  };

  const checkAndRemoveCert = (indx, values, e) => {
    e.preventDefault();
    e.stopPropagation();
    const {
      mutators: { remove, update },
    } = formApi;
    const { isSavedOnce = false } = values;
    if (!isSavedOnce) {
      remove(name, indx);
    } else {
      update(name, indx, { ...(preSavedValues.current[indx] || {}), isEditMode: false });
    }
    delete preSavedValues.current[indx];
  };

  const handleCertSave = (indx, values) => {
    const updatedValues = { ...values, isEditMode: false, isSavedOnce: true };
    const {
      mutators: { update },
    } = formApi;
    update(name, indx, updatedValues);
    delete preSavedValues.current[indx];
  };

  const handleCertEdit = (indx, values, event) => {
    event.preventDefault();
    event.stopPropagation();
    const {
      mutators: { update },
    } = formApi;
    const updatedValues = { ...values, isEditMode: true };
    preSavedValues.current[indx] = cloneDeep(updatedValues);
    update(name, indx, updatedValues);
  };

  const addCertBtnLabel = intl.formatMessage({ id: 'FieldPermitAndCertificate.addCertBtn' });

  const classes = classNames(rootClassName || css.root, className, {
    [errorClassName]: !!errorClassName && hasError,
  });

  const editBtnLabel = intl.formatMessage({ id: 'FieldWorkerJobExperience.editBtn' });
  const removeBtnLabel = intl.formatMessage({ id: 'FieldWorkerJobExperience.removeBtn' });

  const titleClasses = classNames(css.titleWrapper, titleClassName);

  return (
    <React.Fragment>
      <div className={classes} id={id}>
        <div className={titleClasses}>
          <h2 className={css.heading}>
            <FormattedMessage id="FieldPermitAndCertificate.heading" />
          </h2>
          <p className={css.helperText}>
            <FormattedMessage id="FieldPermitAndCertificate.subHeading" />
          </p>
        </div>
        <FieldArray name={name}>
          {({ fields }) => {
            const { value } = fields;
            return fields.map((fieldName, indx) => {
              const savedDetails = value[indx];
              const {
                isEditMode = true,
                permitAndCertificateName,
                permitAndCertificateDesc,
              } = savedDetails;
              const fieldArrayFormError = (formError[name] || [])[indx] || {};
              
              return (
                <div key={`${fieldName}-${indx}`} className={css.formWrapper}>
                  {isEditMode ? (
                    <PermitAndCertificateForm
                      name={fieldName}
                      userNativeLang={userNativeLang}
                      onCancel={checkAndRemoveCert.bind(null, indx, savedDetails)}
                      className={css.form}
                      values={savedDetails}
                      onSubmit={handleCertSave.bind(null, indx)}
                      formError={fieldArrayFormError}
                    />
                  ) : (
                    <div className={css.fieldParentContainer}>
                      <div className={css.heading}>
                        <div className={css.topHeading}>
                          <div>
                            <h3>{permitAndCertificateName}</h3>
                          </div>
                          <div className={css.editRemoveBtnDiv}>
                            <InlineTextButton
                              type="button"
                              onClick={handleCertEdit.bind(null, indx, savedDetails)}
                            >
                              <FiEdit3 />
                              <span>{editBtnLabel}</span>
                            </InlineTextButton>
                            <InlineTextButton
                              type="button"
                              onClick={checkAndRemoveCert.bind(null, indx, {})}
                            >
                              <FiMinus />
                              <span>{removeBtnLabel}</span>
                            </InlineTextButton>
                          </div>
                        </div>
                      </div>
                      <div className={css.footer}>
                        <p>{permitAndCertificateDesc}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            });
          }}
        </FieldArray>
        <InlineTextButton type="button" className={css.addMoreJobBtn} onClick={addMoreCert}>
          <BsPlusLg className={css.icon} />
          <span className={css.text}>{addCertBtnLabel}</span>
        </InlineTextButton>
      </div>
    </React.Fragment>
  );
};

FieldPermitAndCertificateComponent.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  errorClassName: null,
  userNativeLang: languageSelectOptions.en.value,
  values: [],
  formError: {},
};

FieldPermitAndCertificateComponent.propTypes = {
  rootClassName: string,
  className: string,
  errorClassName: string,
  titleClassName: string,
  onChange: func,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string.isRequired,
  name: string.isRequired,

  formApi: object.isRequired,
  userNativeLang: oneOf(nativeLangOptions).isRequired,
  formError: object.isRequired,
};

const FieldPermitAndCertificateComp = injectIntl(FieldPermitAndCertificateComponent);

const FieldPermitAndCertificate = props => {
  return <FieldPermitAndCertificateComp {...props} />;
};

export default FieldPermitAndCertificate;
